import { Flex, Modal } from "antd";
import { FC, useCallback, useEffect, useMemo } from "react";
import {
    getTaskListsThunk,
    getWorkGetScheduleThunk,
    postMessengerReceivedDelivery,
} from "tm-shared/api";
import { countText } from "tm-shared/utils";

import { ProgressLoader } from "@/app/ProgressLoader";
import BaseButton from "@/components/BaseButton";
import { PrimaryText, SecondaryText } from "@/components/Typography/Styled";
import { MANAGER_AGENT_VOCABULARY } from "@/constants/domain/managerAgentVocabulary";
import { isManagerRole } from "@/hooks/isManagerRole";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useAsyncTaskQueue } from "@/hooks/useAsyncTaskQueue";
import {
    getKpiGetMarketProgressesThunk,
    getPlansByMarketIdThunk,
    setUpdateUnscheduledMarketsProcessing,
} from "@/store/slices/offline";
import { ModalPayloads, ModalType } from "@/types/modals";

export interface UnscheduledMarketCompletionModalProps {
    payload: ModalPayloads[ModalType.UnscheduledMarketCompletion];
    onCloseModal: () => void;
}

const UnscheduledMarketCompletionModal: FC<
    UnscheduledMarketCompletionModalProps
> = ({ payload, onCloseModal }) => {
    const dispatch = useAppDispatch();
    const { id: userId, team, role } = useAppSelector((s) => s.user);
    const { activeSchedule, routeList } = useAppSelector((s) => s.sv);

    const { queue, progress } = useAsyncTaskQueue();

    const isManager = useMemo(() => isManagerRole(role), [role]);
    const shouldLoadPlans = useMemo(
        () => role === "sv" || role === "ta",
        [role],
    );

    useEffect(() => {
        if (!payload?.deliveryId) return;

        postMessengerReceivedDelivery({
            deliveryId: payload.deliveryId,
        });
        dispatch(setUpdateUnscheduledMarketsProcessing(false));
    }, [dispatch, payload?.deliveryId]);

    const getScheduleParams = useCallback(() => {
        if (isManager) {
            return {
                userId: activeSchedule,
                roleSlug: MANAGER_AGENT_VOCABULARY[role],
            };
        }
        return { userId };
    }, [isManager, activeSchedule, role, userId]);

    // Получение параметров для загрузки задач
    const getTaskParams = useCallback(
        (marketIds: string[]) => {
            const baseParams = { targetIds: marketIds };
            if (isManager) {
                return {
                    ...baseParams,
                    roleSlug: MANAGER_AGENT_VOCABULARY[role],
                };
            }
            return baseParams;
        },
        [isManager, role],
    );

    const getRouteId = useCallback(() => {
        if (role === "ta") {
            return team?.route?.uuid;
        }
        return Object.values(routeList).find(
            (route) => route.agentId === userId,
        )?.uuid;
    }, [role, team?.route?.uuid, routeList, userId]);

    const handleConfirm = useCallback(async () => {
        try {
            await queue.add("Оновлення розкладу роботи", async () => {
                const { result } = await dispatch(
                    getWorkGetScheduleThunk(getScheduleParams()),
                ).unwrap();

                const marketIds = Object.keys(result?.markets ?? {});
                if (!marketIds.length) return;

                const tasks = [
                    queue.add("Завантаження задач по ТТ", () =>
                        dispatch(
                            getTaskListsThunk(getTaskParams(marketIds)),
                        ).unwrap(),
                    ),
                ];

                if (shouldLoadPlans) {
                    const routeId = getRouteId();
                    if (routeId) {
                        tasks.push(
                            queue.add(
                                "Завантаження інформації про плани на ТТ",
                                async () => {
                                    await dispatch(
                                        getPlansByMarketIdThunk({
                                            marketIds,
                                            routeId,
                                        }),
                                    ).unwrap();
                                    queue.add(
                                        "Завантаження інформації про факти на ТТ",
                                        () =>
                                            dispatch(
                                                getKpiGetMarketProgressesThunk({
                                                    marketIds,
                                                }),
                                            ),
                                    );
                                },
                            ),
                        );
                    }
                }

                await Promise.all(tasks);
            });

            await queue.runParallel();
        } catch (error) {
            console.error("Failed to update work data:", error);
        } finally {
            queue.clear();
            onCloseModal();
        }
    }, [
        dispatch,
        onCloseModal,
        queue,
        getScheduleParams,
        getTaskParams,
        shouldLoadPlans,
        getRouteId,
    ]);

    const isLoaderOpen = useMemo(
        () => progress.isRunning || progress.tasks.length > 0,
        [progress.isRunning, progress.tasks.length],
    );

    return (
        <>
            <Modal
                open
                centered
                closable
                footer={null}
                title={null}
                onCancel={onCloseModal}
            >
                <Flex vertical gap={16}>
                    <PrimaryText
                        fontSize={20}
                        fontWeight="500"
                        style={{ textAlign: "center" }}
                    >
                        Додано {countText.unplanned(payload.marketsCount)} ТТ
                    </PrimaryText>

                    <SecondaryText style={{ textAlign: "center" }}>
                        Оновити робочий план зараз чи пізніше?
                    </SecondaryText>

                    <Flex vertical gap={8}>
                        <BaseButton type="primary" onClick={handleConfirm}>
                            Оновити зараз
                        </BaseButton>

                        <BaseButton type="default" onClick={onCloseModal}>
                            Оновити пізніше
                        </BaseButton>
                    </Flex>
                </Flex>
            </Modal>

            <ProgressLoader open={isLoaderOpen} tasks={progress.tasks} />
        </>
    );
};

export default UnscheduledMarketCompletionModal;
